"use client";

import { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import type { AllStatusData } from '@/lib/types';
import SummaryCard from './summary-card';
import StatusChart from './status-chart';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon, CheckCircle2, FileCheck, Users, Package, Clock, AlertTriangle, GitCompareArrows, Ruler } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format, parse, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import Link from 'next/link';

interface DashboardClientProps {
  initialData: AllStatusData;
  initialFromDate?: string;
}

export default function DashboardClient({ initialData, initialFromDate }: DashboardClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const [data, setData] = useState<AllStatusData>(initialData);
  const [date, setDate] = useState<Date | undefined>(() => {
    if (initialFromDate) {
      const parsedDate = parse(initialFromDate, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        return parsedDate;
      }
    }
    return new Date('2025-09-01');
  });
  const [loading, setLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!date || !isValid(date)) return;
    const fromDateString = format(date, 'yyyy-MM-dd');
    try {
      const res = await fetch(`/api/sales-buz?fromDate=${encodeURIComponent(fromDateString)}`);
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const newData: AllStatusData = await res.json();
      setData(newData);
    } catch (error) {
      console.error('Failed to fetch updated data', error);
    }
  }, [date]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchData();
    }, 60000); // 60000ms = 1 minute

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [fetchData]);


  useEffect(() => {
    const fromDateParam = searchParams.get('fromDate');
    if (fromDateParam) {
      const newDate = parse(fromDateParam, 'yyyy-MM-dd', new Date());
      if (isValid(newDate) && (!date || date?.getTime() !== newDate.getTime())) {
        setDate(newDate);
      }
    }
  }, [searchParams, date]);

  useEffect(() => {
    setData(initialData);
    setLoading(false);
  }, [initialData]);

  const handleDateChange = (newDate: Date | undefined) => {
    if (!newDate || !isValid(newDate)) return;

    setLoading(true);
    setDate(newDate);
    setPopoverOpen(false); // Close the popover
    const current = new URLSearchParams(Array.from(searchParams.entries()));
    const fromDateString = format(newDate, 'yyyy-MM-dd');
    current.set('fromDate', fromDateString);
    
    const search = current.toString();
    const query = search ? `?${search}` : '';
    router.push(`/${query}`);
  };

  const failedCount = (data.open?.count || 0) + (data.customer?.count || 0) + (data.item?.count || 0) + (data.mismatch?.count || 0) + (data.issue?.count || 0) + (data.unite_mismatch?.count || 0);
  
  const buildLink = (status: string) => {
    const params = new URLSearchParams();
    if (date && isValid(date)) {
      params.set('fromDate', format(date, 'yyyy-MM-dd'));
    }
    const queryString = params.toString();
    return `/details/${status}${queryString ? `?${queryString}` : ''}`;
  }


  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-headline tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">A summary of sales order activities.</p>
        </div>
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-full sm:w-[280px] justify-start text-left font-normal",
                !date && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date && isValid(date) ? format(date, "PPP") : <span>Pick a start date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateChange}
              initialFocus
              disabled={(d) => d > new Date()}
            />
          </PopoverContent>
        </Popover>
      </div>

      {loading ? <DashboardSkeleton /> : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <SummaryCard
            title="Ready"
            count={data.ready?.count || 0}
            icon={<FileCheck className="h-6 w-6 text-primary" />}
            link={buildLink('ready')}
            description="Orders ready for processing."
          />
          <SummaryCard
            title="Completed"
            count={data.completed?.count || 0}
            icon={<CheckCircle2 className="h-6 w-6 text-green-500" />}
            link={buildLink('completed')}
            description="Successfully completed orders."
          />
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Failed</CardTitle>
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{failedCount}</div>
              <p className="text-xs text-muted-foreground">Orders that require attention.</p>
              <div className="mt-4 flex flex-col space-y-1 text-sm">
                <Link href={buildLink('open')} className="flex items-center justify-between rounded-md p-2 -mx-2 hover:bg-muted">
                  <span className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" /> Open
                  </span>
                  <span className="font-medium text-primary">{data?.open?.count || 0}</span>
                </Link>
                <Link href={buildLink('customer')} className="flex items-center justify-between rounded-md p-2 -mx-2 hover:bg-muted">
                  <span className="flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" /> Customer Issue
                  </span>
                  <span className="font-medium text-primary">{data.customer?.count || 0}</span>
                </Link>
                <Link href={buildLink('item')} className="flex items-center justify-between rounded-md p-2 -mx-2 hover:bg-muted">
                  <span className="flex items-center gap-2 text-muted-foreground">
                      <Package className="h-4 w-4" /> Item Issue
                  </span>
                  <span className="font-medium text-primary">{data.item?.count || 0}</span>
                </Link>
                <Link href={buildLink('mismatch')} className="flex items-center justify-between rounded-md p-2 -mx-2 hover:bg-muted">
                  <span className="flex items-center gap-2 text-muted-foreground">
                      <GitCompareArrows className="h-4 w-4" /> Mismatch
                  </span>
                  <span className="font-medium text-primary">{data.mismatch?.count || 0}</span>
                </Link>
                <Link href={buildLink('issue')} className="flex items-center justify-between rounded-md p-2 -mx-2 hover:bg-muted">
                  <span className="flex items-center gap-2 text-muted-foreground">
                      <AlertTriangle className="h-4 w-4" /> General Issue
                  </span>
                  <span className="font-medium text-primary">{data.issue?.count || 0}</span>
                </Link>
                <Link href={buildLink('unite_mismatch')} className="flex items-center justify-between rounded-md p-2 -mx-2 hover:bg-muted">
                  <span className="flex items-center gap-2 text-muted-foreground">
                      <Ruler className="h-4 w-4" /> Unite Mismatch
                  </span>
                  <span className="font-medium text-primary">{data.unite_mismatch?.count || 0}</span>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {loading ? <ChartSkeleton /> : (
        <Card>
          <CardHeader>
            <CardTitle>Status Overview</CardTitle>
          </CardHeader>
          <CardContent className="pl-2">
            <StatusChart data={data} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const DashboardSkeleton = () => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {[...Array(3)].map((_, i) => (
        <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-6 w-6 rounded-full" />
            </CardHeader>
            <CardContent>
                <Skeleton className="h-8 w-12" />
                <Skeleton className="h-4 w-32 mt-2" />
            </CardContent>
        </Card>
      ))}
    </div>
)

const ChartSkeleton = () => (
    <Card>
        <CardHeader>
            <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent>
            <Skeleton className="h-[350px] w-full" />
        </CardContent>
    </Card>
)
